import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  GeneralButton,
  GeneralInput,
  Loader,
  Modal,
} from "../../../components";
import { useAppDispatch, useAppSelector, usePostData } from "../../../hooks";
import { resetOldPassword } from "../../../redux/reducers/changePasswordSlice";
import { resetUserDetails } from "../../../redux/reducers/registrationSlice";
import { resetUserState } from "../../../redux/reducers/userSlice";
import { persistor } from "../../../redux/store";
import AuthLayout from "../../../Layout/AuthLayout";
import { EndPoints } from "../../../Constants/endpoints";
import { useToast } from "../../../Services/ToastContext";
import SecureIcon from "../../../assets/images/icons/secure.svg";

interface NewPasswordFormInputs {
  NewPassword: string;
}

interface PasswordValidationState {
  isUpperCasePresent: boolean;
  isNumberPresent: boolean;
  isPasswordLongEnough: boolean;
}

const newPasswordSchema = z.object({
  NewPassword: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long" })
    .regex(/[A-Z]/, {
      message: "Password must contain at least one uppercase letter",
    })
    .regex(/\d/, { message: "Password must contain at least one number" }),
});

const ChangePassword: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const oldPassword = useAppSelector(
    (state) => state.changePassword.oldPassword,
  );
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate, isPending } = usePostData(EndPoints.ChangePassword);

  const isProgrammaticNavigation = useRef(false); 

  const methods = useForm<NewPasswordFormInputs>({
    defaultValues: {
      NewPassword: "",
    },
    resolver: zodResolver(newPasswordSchema),
    mode: "onChange",
  });

  const { handleSubmit, watch } = methods;
  const newPassword = watch("NewPassword", "");

  const validatePasswordChars: PasswordValidationState = {
    isUpperCasePresent: /[A-Z]/.test(newPassword),
    isNumberPresent: /\d/.test(newPassword),
    isPasswordLongEnough: newPassword.length >= 8,
  };

  const criteriaMetCount = Object.values(validatePasswordChars).filter(
    Boolean,
  ).length;

  const totalLines = 3;

  const getLineColor = (index: number, totalMet: number): string => {
    if (index >= totalMet) return "bg-primary-bg";
    switch (totalMet) {
      case 1:
        return "bg-error";
      case 2:
        return "bg-orange";
      case 3:
        return "bg-successGreen";
      default:
        return "bg-primary-bg";
    }
  };

  const getFeedbackTextColor = (isMet: boolean, currentPassword: string) => {
    if (!currentPassword) {
      return "text-body-text";
    }
    return isMet ? "text-successGreen" : "text-error";
  };

  const isPasswordValid = criteriaMetCount === totalLines;

  const onSubmit: SubmitHandler<NewPasswordFormInputs> = (data) => {
    if (!data.NewPassword) {
      setError(true);
      setErrorMessage(t("all_fields_required"));
      return;
    }

    const requestData = {
      Password: oldPassword,
      NewPassword: data.NewPassword,
    };

    mutate(requestData, {
      onSuccess: () => {
        setIsModalOpen(true);
      },
      onError: (error: any) => {
        setError(true);
        showToast(error.response?.data?.ErrorMessage || error.message, "error");
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <AuthLayout
          title={t("enter_new_password")}
          description={t("create_your_new_password_msg")}
          lottieImage={null}
        >
          <h2 id="new-password-title" className="sr-only">
            {t("enter_new_password")}
          </h2>

          <form
            className="w-full"
            onSubmit={handleSubmit(onSubmit)}
            aria-labelledby="new-password-title"
          >
            <div className="mb-2 mt-6">
              <GeneralInput
                type="password"
                name="NewPassword"
                label={t("new_password")}
                placeholder={t("new_password")}
                required
              />
            </div>

            <div className="password-feedback-container mt-2 flex w-full gap-1">
              {[...Array(totalLines)].map((_, index) => (
                <div
                  key={index}
                  className={`h-1 w-1/3 rounded ${getLineColor(index, criteriaMetCount)}`}
                ></div>
              ))}
            </div>

            <ul className="my-4 space-y-1 text-sm">
              <li
                className={getFeedbackTextColor(
                  validatePasswordChars.isUpperCasePresent,
                  newPassword,
                )}
              >
                {t("at_least_one_uppercase_letter")}
              </li>
              <li
                className={getFeedbackTextColor(
                  validatePasswordChars.isNumberPresent,
                  newPassword,
                )}
              >
                {t("must_contain_numbers")}
              </li>
              <li
                className={getFeedbackTextColor(
                  validatePasswordChars.isPasswordLongEnough,
                  newPassword,
                )}
              >
                {t("at_least_8_characters")}
              </li>
            </ul>

            {error && (
              <p
                className="mb-4 text-center text-error"
                role="alert"
                aria-live="assertive"
              >
                {errorMessage}
              </p>
            )}

            <GeneralButton
              type="submit"
              title={t("change_password")}
              color="blue"
              block
              disabled={isPending || !isPasswordValid}
              ariaLabel={t("change_password")}
            />
            {isPending && <Loader />}
          </form>
        </AuthLayout>
      </FormProvider>

      <Modal isOpen={isModalOpen} showClose>
        <div className="flex flex-col items-center justify-center">
          <img src={SecureIcon} className="h-24" alt="secure" />
          <h3 className="mb-2 mt-6 text-lg font-bold">
            {" "}
            {t("account_secured_now")}
          </h3>
          <p className="text-body-text text-sm">
            {t("password_created_successfully")}
          </p>
          <GeneralButton
            className="mb-4 mt-8"
            onClick={() => {
              isProgrammaticNavigation.current = true;
              dispatch(resetOldPassword());
              localStorage.removeItem("token");
              dispatch(resetUserDetails());
              dispatch(resetUserState());
              persistor.purge();
              navigate("/login", { replace: true });
            }}
            title={t("continue")}
          />
        </div>
      </Modal>
    </>
  );
};

export default ChangePassword;
